import React, { useState } from 'react';
import { Modal, ModalOverlay, ModalContent, ModalBody } from '@chakra-ui/react';
import { MdClose } from 'react-icons/md';
import { Zap, AlertTriangle } from 'lucide-react';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import { Task, Priority, TaskStatus, OKRState } from '../types';

// ── Score slider row ──────────────────────────────────────────────────────────

const ScoreSlider: React.FC<{
    label: string;
    value: number;
    onChange: (v: number) => void;
    color: string;
}> = ({ label, value, onChange, color }) => (
    <div>
        <div className="flex justify-between items-center mb-1.5">
            <label className="text-sm font-medium text-navy-700">{label}</label>
            <span className="text-sm font-bold" style={{ color }}>{value} / 10</span>
        </div>
        <input
            type="range" min={1} max={10} value={value}
            onChange={(e) => onChange(parseInt(e.target.value))}
            className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
            style={{ accentColor: color }}
        />
    </div>
);

// ── Component ─────────────────────────────────────────────────────────────────

const CreateTaskModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (t: Task) => void;
    state: OKRState;
    // Edit mode
    editTask?: Task;
    onUpdate?: (id: string, data: Record<string, any>) => void;
}> = ({ isOpen, onClose, onSubmit, state, editTask, onUpdate }) => {
    const isEdit = !!editTask;

    const [title, setTitle] = useState(editTask?.title ?? '');
    const [description, setDescription] = useState(editTask?.description ?? '');
    const [assigneeId, setAssigneeId] = useState(editTask?.assigneeId ?? '');
    const [dueDate, setDueDate] = useState(editTask?.dueDate?.split('T')[0] ?? '');
    const [priority, setPriority] = useState<Priority>(editTask?.priority ?? 'medium');
    const [objectiveId, setObjectiveId] = useState(editTask?.objectiveId ?? '');
    const [estimatedHours, setEstimatedHours] = useState(String(editTask?.estimatedHours ?? ''));
    const [spentHours, setSpentHours] = useState(String(editTask?.spentHours ?? ''));
    const [status, setStatus] = useState<TaskStatus>(editTask?.status ?? 'todo');
    const [impactKPI, setImpactKPI] = useState(editTask?.impactKPI ?? 5);
    const [urgency, setUrgency] = useState(editTask?.urgency ?? 5);
    const [effort, setEffort] = useState(editTask?.effort ?? 5);
    const [blockedReason, setBlockedReason] = useState(editTask?.blockedReason ?? '');

    // Sync when editTask changes (re-opening with different task)
    React.useEffect(() => {
        if (editTask) {
            setTitle(editTask.title);
            setDescription(editTask.description ?? '');
            setAssigneeId(editTask.assigneeId);
            setDueDate(editTask.dueDate?.split('T')[0] ?? '');
            setPriority(editTask.priority);
            setObjectiveId(editTask.objectiveId ?? '');
            setEstimatedHours(String(editTask.estimatedHours ?? ''));
            setSpentHours(String(editTask.spentHours ?? ''));
            setStatus(editTask.status);
            setImpactKPI(editTask.impactKPI);
            setUrgency(editTask.urgency);
            setEffort(editTask.effort);
            setBlockedReason(editTask.blockedReason ?? '');
        }
    }, [editTask]);

    const smartScore = effort > 0 ? Math.round(((impactKPI * urgency) / effort) * 10) / 10 : 0;
    const scoreColor = smartScore >= 15 ? '#ea0606' : smartScore >= 8 ? '#FFB547' : '#A3AED0';

    const reset = () => {
        setTitle(''); setDescription(''); setAssigneeId(''); setDueDate('');
        setPriority('medium'); setObjectiveId(''); setEstimatedHours('');
        setSpentHours(''); setStatus('todo'); setBlockedReason('');
        setImpactKPI(5); setUrgency(5); setEffort(5);
    };
    const handleClose = () => { if (!isEdit) reset(); onClose(); };

    const handleSubmit = () => {
        if (!title.trim() || !dueDate) return;

        if (isEdit && onUpdate && editTask) {
            onUpdate(editTask.id || editTask._id || '', {
                title: title.trim(),
                description: description.trim(),
                assigneeId,
                dueDate,
                priority,
                objectiveId: objectiveId || null,
                estimatedHours: parseFloat(estimatedHours) || 0,
                spentHours: parseFloat(spentHours) || 0,
                status,
                impactKPI, urgency, effort,
                smart_score: smartScore,
                blockedReason: status === 'blocked' ? blockedReason : '',
            });
            onClose();
            return;
        }

        if (!assigneeId) return;
        const task: Task = {
            id: `task-${Date.now()}`, actionPlanId: '', objectiveId,
            title: title.trim(), description: description.trim(),
            assigneeId, dueDate, priority,
            estimatedHours: parseFloat(estimatedHours) || 0,
            spentHours: 0, status: 'todo',
            impactKPI, urgency, effort, smart_score: smartScore,
        };
        onSubmit(task);
        reset(); onClose();
    };

    const sInput = 'w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-toola-500';
    const priorities: { key: Priority; label: string }[] = [
        { key: 'critical', label: 'Critique' }, { key: 'high', label: 'Haute' },
        { key: 'medium', label: 'Moyenne' }, { key: 'low', label: 'Faible' },
    ];
    const pColors: Record<Priority, string> = {
        critical: 'border-red-400 bg-red-50 text-red-600',
        high: 'border-orange-400 bg-orange-50 text-orange-600',
        medium: 'border-amber-400 bg-amber-50 text-amber-600',
        low: 'border-gray-300 bg-gray-50 text-gray-500',
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="lg">
            <ModalOverlay className="bg-[#000] !opacity-50 backdrop-blur-sm" />
            <ModalContent className="md:top-[5vh] !overflow-hidden !z-[1002]">
                <ModalBody className="p-0">
                    <Card extra="p-6 max-h-[88vh] overflow-y-auto">

                        {/* Header */}
                        <div className="flex items-start justify-between mb-5 pb-4 border-b border-gray-100">
                            <div>
                                <h2 className="text-xl font-semibold text-navy-700">{isEdit ? 'Modifier la tâche' : 'Nouvelle tâche'}</h2>
                                <p className="text-sm text-gray-500 mt-0.5">{isEdit ? editTask?.title : 'Créer et prioriser en moins de 10 secondes'}</p>
                            </div>
                            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 mt-0.5">
                                <MdClose className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-5">
                            <InputField
                                label="Titre *"
                                placeholder="Ex : Audit des procédures qualité"
                                id="task-title"
                                type="text"
                                value={title}
                                onChange={(e: any) => setTitle(e.target.value)}
                            />

                            <div>
                                <label className="text-sm font-medium text-navy-700 mb-2 block">Description</label>
                                <textarea
                                    rows={2}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Détails optionnels…"
                                    className={`${sInput} resize-none`}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-navy-700 mb-2 block">Assigné à *</label>
                                    {state.team.length === 0 ? (
                                        <div className="flex items-center gap-2 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2.5 text-sm text-orange-600">
                                            <AlertTriangle size={14} className="flex-shrink-0" />
                                            Aucun membre — connectez-vous à l'API pour charger l'équipe
                                        </div>
                                    ) : (
                                        <select
                                            value={assigneeId}
                                            onChange={(e) => setAssigneeId(e.target.value)}
                                            className={sInput}
                                        >
                                            <option value="">Sélectionner…</option>
                                            {state.team.map((m) => (
                                                <option key={m.id} value={m.id}>{m.name}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                                <InputField
                                    label="Date limite *"
                                    id="task-due"
                                    type="date"
                                    value={dueDate}
                                    onChange={(e: any) => setDueDate(e.target.value)}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-navy-700 mb-2 block">Objectif lié</label>
                                    <select
                                        value={objectiveId}
                                        onChange={(e) => setObjectiveId(e.target.value)}
                                        className={sInput}
                                    >
                                        <option value="">Aucun</option>
                                        {state.objectives.map((o) => (
                                            <option key={o.id} value={o.id}>{o.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <InputField
                                    label="Temps estimé (h)"
                                    id="task-hours"
                                    type="number"
                                    placeholder="0"
                                    value={estimatedHours}
                                    onChange={(e: any) => setEstimatedHours(e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="text-sm font-medium text-navy-700 mb-2 block">Priorité</label>
                                <div className="grid grid-cols-4 gap-2">
                                    {priorities.map(({ key, label }) => (
                                        <button
                                            key={key}
                                            onClick={() => setPriority(key)}
                                            className={`py-2 text-xs font-semibold rounded-xl border-2 transition-all ${
                                                priority === key
                                                    ? pColors[key]
                                                    : 'border-gray-100 text-gray-400 hover:border-gray-200'
                                            }`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Status + heures passées — edit mode only */}
                            {isEdit && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-navy-700 mb-2 block">Statut</label>
                                        <select value={status} onChange={(e) => setStatus(e.target.value as TaskStatus)} className={sInput}>
                                            <option value="todo">À faire</option>
                                            <option value="in-progress">En cours</option>
                                            <option value="waiting">En attente</option>
                                            <option value="blocked">Bloquée</option>
                                            <option value="done">Terminée</option>
                                        </select>
                                    </div>
                                    <InputField
                                        label="Heures passées"
                                        id="task-spent"
                                        type="number"
                                        placeholder="0"
                                        value={spentHours}
                                        onChange={(e: any) => setSpentHours(e.target.value)}
                                    />
                                </div>
                            )}

                            {/* Raison de blocage */}
                            {status === 'blocked' && (
                                <div>
                                    <label className="text-sm font-medium text-navy-700 mb-2 block">Raison du blocage</label>
                                    <textarea
                                        rows={2}
                                        value={blockedReason}
                                        onChange={(e) => setBlockedReason(e.target.value)}
                                        placeholder="Décrivez ce qui bloque…"
                                        className={`${sInput} resize-none`}
                                    />
                                </div>
                            )}

                            {/* Smart score calculator */}
                            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-navy-700">Score d'impact</p>
                                        <p className="text-xs text-gray-400 mt-0.5">Smart score = (Impact × Urgence) / Effort</p>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-xl font-bold" style={{ color: scoreColor }}>
                                        <Zap size={16} />
                                        {smartScore}
                                    </div>
                                </div>
                                <ScoreSlider label="Impact sur KPI" value={impactKPI} onChange={setImpactKPI} color="#0260CB" />
                                <ScoreSlider label="Urgence" value={urgency} onChange={setUrgency} color="#FFB547" />
                                <ScoreSlider label="Effort requis" value={effort} onChange={setEffort} color="#A3AED0" />
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="mt-6 flex gap-3 pt-4 border-t border-gray-100">
                            <button onClick={handleClose} className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                                Annuler
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={!title.trim() || (!isEdit && !assigneeId) || !dueDate}
                                className="linear flex-1 rounded-xl bg-toola-500 py-2.5 text-sm font-medium text-white transition duration-200 hover:bg-toola-600 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {isEdit ? 'Enregistrer' : 'Créer la tâche'}
                            </button>
                        </div>
                    </Card>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default CreateTaskModal;
